/**
 * Auth API — pure async functions, port z js/auth.js (logika 1:1).
 * Wszystkie funkcje używają Firebase JS SDK v9 modular.
 */
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as fbSignOut,
  sendPasswordResetEmail,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  getAdditionalUserInfo,
  updateProfile,
  type User as FirebaseUser,
} from 'firebase/auth';
import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  query,
  where,
  writeBatch,
  serverTimestamp,
  updateDoc,
  arrayUnion,
  limit,
} from 'firebase/firestore';
import { auth, db, COLLECTIONS, IS_DEV_MODE } from '../../lib/firebase';
import { generateFamilyCode } from '../../lib/utils';

// ── Mapowanie kodów Firebase → polskie komunikaty ──
const AUTH_ERRORS: Record<string, string> = {
  'auth/user-not-found': 'Nie znaleziono konta z tym adresem email',
  'auth/wrong-password': 'Nieprawidłowe hasło',
  'auth/invalid-credential': 'Nieprawidłowy email lub hasło',
  'auth/email-already-in-use': 'Ten adres email jest już zajęty',
  'auth/weak-password': 'Hasło musi mieć min. 6 znaków',
  'auth/invalid-email': 'Nieprawidłowy format adresu email',
  'auth/too-many-requests': 'Zbyt wiele prób — spróbuj za chwilę',
  'auth/popup-closed-by-user': 'Okno logowania zostało zamknięte',
  'auth/network-request-failed': 'Błąd sieci — sprawdź połączenie',
  'auth/cancelled-popup-request': 'Okno logowania zostało zamknięte',
  'auth/account-exists-with-different-credential': 'Konto z tym emailem istnieje z innym sposobem logowania',
};

export const getAuthErrorMessage = (code: string | undefined): string =>
  (code && AUTH_ERRORS[code]) ?? 'Wystąpił błąd. Spróbuj ponownie.';

// ── Detekcja Safari/iOS/PWA → redirect (popup zawodny) ──
export function shouldUseRedirect(): boolean {
  const ua = navigator.userAgent || '';
  if (/iPhone|iPad|iPod/i.test(ua)) return true;
  if (/^((?!chrome|chromium|edg|firefox|android).)*safari/i.test(ua)) return true;
  if (window.matchMedia?.('(display-mode: standalone)').matches) return true;
  if (/Android/i.test(ua)) return true;
  return false;
}

// ── Login ─────────────────────────────────────
export async function signIn(email: string, password: string, remember: boolean): Promise<FirebaseUser> {
  await setPersistence(auth, remember ? browserLocalPersistence : browserSessionPersistence);
  const cred = await signInWithEmailAndPassword(auth, email, password);
  return cred.user;
}

// ── Register ──────────────────────────────────
export async function signUp(email: string, password: string, displayName: string): Promise<FirebaseUser> {
  const cred = await createUserWithEmailAndPassword(auth, email, password);
  await updateProfile(cred.user, { displayName });
  return cred.user;
}

export async function signOut(): Promise<void> {
  if (IS_DEV_MODE) {
    sessionStorage.removeItem('rf-dev-auth');
    const { useAuthStore } = await import('./authStore');
    const { useWalletsStore } = await import('../wallets/walletsStore');
    const { useTransactionsStore } = await import('../transactions/transactionsStore');
    const { useCategoriesStore } = await import('../categories/categoriesStore');
    const { useGoalsStore } = await import('../goals/goalsStore');
    const { useBudgetsStore } = await import('../budgets/budgetsStore');
    useAuthStore.getState().reset();
    useWalletsStore.getState().reset();
    useTransactionsStore.getState().reset();
    useCategoriesStore.getState().reset();
    useGoalsStore.getState().reset();
    useBudgetsStore.getState().reset();
    window.location.hash = '#/login';
    return;
  }
  await fbSignOut(auth);
}

/** Opuść rodzinę — usuwa członka z `families/{familyId}.members.{uid}`.
 *  Nie usuwa transakcji ani portfeli (audytowalność danych historycznych). */
export async function leaveFamily(uid: string, familyId: string): Promise<void> {
  if (IS_DEV_MODE) return;
  const { deleteField } = await import('firebase/firestore');
  await updateDoc(doc(db, COLLECTIONS.families, familyId), {
    [`members.${uid}`]: deleteField(),
  });
}

/** Usuń konto Firebase Auth (current user). Wymaga niedawnego logowania. */
export async function deleteAccount(): Promise<void> {
  if (IS_DEV_MODE) return;
  if (!auth.currentUser) throw new Error('no-user');
  await auth.currentUser.delete();
}

// ── Reset password ────────────────────────────
export async function sendPasswordReset(email: string): Promise<void> {
  await sendPasswordResetEmail(auth, email);
}

// ── Google OAuth ──────────────────────────────
/** Zwraca null jeśli redirect — strona przeładowuje się i wynik obsłuży getRedirectResult po powrocie. */
export async function signInWithGoogle(): Promise<{ user: FirebaseUser; isNewUser: boolean } | null> {
  const provider = new GoogleAuthProvider();
  if (shouldUseRedirect()) {
    await signInWithRedirect(auth, provider);
    return null;
  }
  const result = await signInWithPopup(auth, provider);
  const info = getAdditionalUserInfo(result);
  return { user: result.user, isNewUser: info?.isNewUser ?? false };
}

/** Sprawdź wynik redirect po powrocie z Google (wywołane na mount LoginPage/RegisterPage). */
export async function consumeGoogleRedirect(): Promise<{ user: FirebaseUser; isNewUser: boolean } | null> {
  try {
    const result = await getRedirectResult(auth);
    if (!result) return null;
    const info = getAdditionalUserInfo(result);
    return { user: result.user, isNewUser: info?.isNewUser ?? false };
  } catch (err) {
    console.error('[Auth] getRedirectResult error:', err);
    throw err;
  }
}

// ── Default categories V2 (Spendee-like) ──────
const DEFAULT_CATEGORIES_V2 = [
  { name: 'Zakupy spożywcze', icon: 'shopping_cart', color: '#4CAF50', type: 'expense' as const, order: 1 },
  { name: 'Restauracje', icon: 'restaurant', color: '#FF9800', type: 'expense' as const, order: 2 },
  { name: 'Zakupy', icon: 'shopping_bag', color: '#E91E63', type: 'expense' as const, order: 3 },
  { name: 'Transport', icon: 'car', color: '#2196F3', type: 'expense' as const, order: 4 },
  { name: 'Zdrowie', icon: 'health', color: '#F44336', type: 'expense' as const, order: 5 },
  { name: 'Dom', icon: 'home', color: '#795548', type: 'expense' as const, order: 6 },
  { name: 'Rachunki', icon: 'bills', color: '#607D8B', type: 'expense' as const, order: 7 },
  { name: 'Dzieci', icon: 'child', color: '#FF4081', type: 'expense' as const, order: 8 },
  { name: 'Rozrywka', icon: 'movie', color: '#9C27B0', type: 'expense' as const, order: 9 },
  { name: 'Sport i hobby', icon: 'sport', color: '#00BCD4', type: 'expense' as const, order: 10 },
  { name: 'Uroda', icon: 'sparkles', color: '#EC407A', type: 'expense' as const, order: 11 },
  { name: 'Podróże', icon: 'travel', color: '#039BE5', type: 'expense' as const, order: 12 },
  { name: 'Edukacja', icon: 'education', color: '#3F51B5', type: 'expense' as const, order: 13 },
  { name: 'Prezenty', icon: 'gift', color: '#FFC107', type: 'expense' as const, order: 14 },
  { name: 'Zwierzęta', icon: 'pets', color: '#8D6E63', type: 'expense' as const, order: 15 },
  { name: 'Inwestycje', icon: 'trending_up', color: '#43A047', type: 'expense' as const, order: 16 },
  { name: 'Inne', icon: 'more', color: '#9E9E9E', type: 'expense' as const, order: 17 },
  { name: 'Wynagrodzenie', icon: 'banknote', color: '#4CAF50', type: 'income' as const, order: 1 },
  { name: 'Premia', icon: 'gift', color: '#8BC34A', type: 'income' as const, order: 2 },
  { name: '800+', icon: 'family', color: '#00BCD4', type: 'income' as const, order: 3 },
  { name: 'Zwrot', icon: 'bank', color: '#1976D2', type: 'income' as const, order: 4 },
  { name: 'Inne przychody', icon: 'coins', color: '#9E9E9E', type: 'income' as const, order: 5 },
];

async function createDefaultCategories(familyId: string): Promise<void> {
  const batch = writeBatch(db);
  for (const cat of DEFAULT_CATEGORIES_V2) {
    const ref = doc(collection(db, COLLECTIONS.categories));
    batch.set(ref, { ...cat, familyId, isDefault: true });
  }
  await batch.commit();
}

// ── Family creation ───────────────────────────
export async function createFamily(
  uid: string,
  creatorName: string,
  creatorEmail: string,
  familyName: string,
): Promise<{ familyId: string; inviteCode: string }> {
  const familyId = doc(collection(db, COLLECTIONS.families)).id;
  const inviteCode = generateFamilyCode();
  const inviteCodeExpiresAt = new Date(Date.now() + 30 * 60 * 1000); // 30 min

  const batch = writeBatch(db);
  const familyRef = doc(db, COLLECTIONS.families, familyId);

  batch.set(familyRef, {
    name: familyName,
    inviteCode,
    inviteCodeExpiresAt,
    creatorId: uid,
    createdAt: serverTimestamp(),
    members: {
      [uid]: { name: creatorName, email: creatorEmail, joinedAt: new Date() },
    },
    // Array-contains query field — Firestore wymaga osobnej tablicy bo nested
    // map field queries (where('members.uid.email', '!=', '')) są zawodne.
    memberIds: [uid],
    categoriesVersion: 2,
  });

  // Wspólny portfel
  const sharedWalletRef = doc(collection(db, COLLECTIONS.wallets));
  batch.set(sharedWalletRef, {
    name: 'Wspólny',
    ownerId: 'shared',
    familyId,
    color: '#E53935',
    type: 'shared',
    balance: 0,
    createdAt: serverTimestamp(),
  });

  // Osobisty portfel twórcy
  const personalWalletRef = doc(collection(db, COLLECTIONS.wallets));
  batch.set(personalWalletRef, {
    name: creatorName,
    ownerId: uid,
    familyId,
    color: '#1976D2',
    type: 'personal',
    balance: 0,
    createdAt: serverTimestamp(),
  });

  await batch.commit();
  await createDefaultCategories(familyId);

  return { familyId, inviteCode };
}

// ── Join family by code ───────────────────────
export async function joinFamilyByCode(
  uid: string,
  userName: string,
  userEmail: string,
  inviteCode: string,
): Promise<string> {
  const cleanCode = inviteCode.toUpperCase().replace(/[-\s]/g, '');
  const snap = await getDocs(query(collection(db, COLLECTIONS.families), where('inviteCode', '==', cleanCode)));
  if (snap.empty) throw new Error('invalid-invite-code');

  const familyDoc = snap.docs[0]!;
  const data = familyDoc.data();

  if (data.inviteCodeExpiresAt) {
    const expiresAt = data.inviteCodeExpiresAt.toDate ? data.inviteCodeExpiresAt.toDate() : new Date(data.inviteCodeExpiresAt);
    if (expiresAt < new Date()) throw new Error('invite-code-expired');
  }

  await updateDoc(familyDoc.ref, {
    [`members.${uid}`]: { name: userName, email: userEmail, joinedAt: new Date() },
    memberIds: arrayUnion(uid),
  });

  // Stwórz osobisty portfel
  await setDoc(doc(collection(db, COLLECTIONS.wallets)), {
    name: userName,
    ownerId: uid,
    familyId: familyDoc.id,
    color: '#1976D2',
    type: 'personal',
    balance: 0,
    createdAt: serverTimestamp(),
  });

  return familyDoc.id;
}

// ── Sprawdź czy user ma portfel (naprawa uwięzionych sesji) ──
export async function ensurePersonalWallet(uid: string, familyId: string, displayName: string): Promise<void> {
  try {
    const snap = await getDocs(
      query(
        collection(db, COLLECTIONS.wallets),
        where('ownerId', '==', uid),
        where('familyId', '==', familyId),
      ),
    );
    if (snap.empty) {
      await setDoc(doc(collection(db, COLLECTIONS.wallets)), {
        name: displayName || 'Mój portfel',
        ownerId: uid,
        familyId,
        color: '#1976D2',
        type: 'personal',
        balance: 0,
        createdAt: serverTimestamp(),
      });
    }
  } catch (err) {
    console.warn('[Auth] ensurePersonalWallet:', err);
  }
}

// ── Find user's family ────────────────────────
const FAMILY_CACHE_KEY = 'rf_family_id';

export async function findUserFamily(uid: string): Promise<{ id: string; data: Record<string, unknown> } | null> {
  // Próba 1: query po `memberIds` (array-contains) — niezawodne, jedna kolekcja indeks.
  try {
    const snap = await getDocs(
      query(
        collection(db, COLLECTIONS.families),
        where('memberIds', 'array-contains', uid),
        limit(1),
      ),
    );
    if (!snap.empty) {
      const docSnap = snap.docs[0]!;
      try { localStorage.setItem(FAMILY_CACHE_KEY, docSnap.id); } catch { /* private mode */ }
      return { id: docSnap.id, data: docSnap.data() };
    }
  } catch (err) {
    console.warn('[Auth] memberIds query failed, fallback to cache:', err);
  }

  // Próba 2: legacy fallback — odczytaj cached familyId z localStorage,
  // sprawdź getDoc + zmigruj (dopisz memberIds) jeśli stary dokument.
  let cachedId: string | null = null;
  try { cachedId = localStorage.getItem(FAMILY_CACHE_KEY); } catch { /* private mode */ }
  // Stary klucz z legacy vanilla
  if (!cachedId) {
    try { cachedId = localStorage.getItem('fw_family_id'); } catch { /* ignore */ }
  }
  if (cachedId) {
    try {
      const docSnap = await getDoc(doc(db, COLLECTIONS.families, cachedId));
      const data = docSnap.exists() ? docSnap.data() : null;
      if (data && (data.members as Record<string, unknown> | undefined)?.[uid]) {
        // Migracja: dopisz memberIds żeby kolejny query działał
        const mids = Array.isArray(data.memberIds) ? data.memberIds : [];
        if (!mids.includes(uid)) {
          try {
            await updateDoc(doc(db, COLLECTIONS.families, cachedId), {
              memberIds: arrayUnion(uid),
            });
          } catch { /* migration best-effort */ }
        }
        return { id: docSnap.id, data };
      }
      // Cached invalid → wyczyść
      try { localStorage.removeItem(FAMILY_CACHE_KEY); localStorage.removeItem('fw_family_id'); } catch { /* ignore */ }
    } catch (err) {
      console.warn('[Auth] cached familyId getDoc failed:', err);
    }
  }
  return null;
}

export async function getFamilyById(familyId: string): Promise<Record<string, unknown> | null> {
  const snap = await getDoc(doc(db, COLLECTIONS.families, familyId));
  return snap.exists() ? snap.data() : null;
}

// Re-export arrayUnion in case other modules need it for member operations.
export { arrayUnion };
